import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getRuns, searchRestrictedOpportunities } from "../api/client.js";
import { Shell } from "../components/Shell.jsx";
import { ErrorCard, Loading, Panel, RestrictedBoundary, StatusPill, formatCount } from "../components/ui.jsx";
import { DOMAIN_LABELS } from "../domainModel.js";
import { ACTIVATION_PATHS } from "../navConfig.js";

export function RestrictedOpportunityReview({ tenant, auth }) {
  const [programDomain, setProgramDomain] = useState("msk");
  const [runMonth, setRunMonth] = useState("");
  const [runs, setRuns] = useState([]);
  const [state, setState] = useState("all");
  const [memberId, setMemberId] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setData(null);
    setError(null);
    getRuns(programDomain)
      .then((result) => {
        const available = result.runs || [];
        setRuns(available);
        setRunMonth(available[0]?.runMonth || "");
      })
      .catch(setError);
  }, [programDomain]);

  const search = async (page = 1) => {
    setBusy(true);
    setError(null);
    try {
      setData(
        await searchRestrictedOpportunities({
          programDomain,
          runMonth,
          state,
          memberId: memberId.trim(),
          page,
          pageSize: 50,
        })
      );
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const clearResults = () => {
    setData(null);
    setMemberId("");
    setError(null);
  };

  if (!auth?.canViewRestrictedOpportunities) {
    return (
      <Shell
        tenant={tenant}
        auth={auth}
        activeNav="secure-review"
        title="Secure Review"
        subtitle="Restricted exact-person evidence is isolated from aggregate workflows"
      >
        <RestrictedBoundary authorized={false} returnHref={ACTIVATION_PATHS.overview}>
          <p>
            Request the opportunity reviewer role through your organization’s access process. Do not use
            campaign or aggregate screens to recreate person-level review.
          </p>
        </RestrictedBoundary>
      </Shell>
    );
  }

  return (
    <Shell
      tenant={tenant}
      auth={auth}
      activeNav="secure-review"
      title="Secure Review"
      subtitle="Restricted exact-person evidence for authorized review only"
      headerRight={
        <Link className="bi-btn-ghost" to={ACTIVATION_PATHS.overview}>
          Aggregate overview
        </Link>
      }
    >
      <RestrictedBoundary authorized returnHref={ACTIVATION_PATHS.overview}>
        <ul className="restricted-boundary-rules">
          <li>Every successful search is tenant-scoped and access-audited.</li>
          <li>Results are displayed for review only; do not capture, copy, export, or document them.</li>
          <li>
            Do not transfer identifiers, evidence, or filters into campaign targeting or message content.
          </li>
        </ul>
      </RestrictedBoundary>

      <div className="restricted-workspace" aria-label="Restricted exact-person review workspace">
        <Panel
          title="Search restricted opportunity evidence"
          desc="Select an evidence period and review state. Exact-person search is optional and never appears in the URL."
        >
          <div className="restricted-search-grid">
            <label>
              Program
              <select value={programDomain} onChange={(event) => setProgramDomain(event.target.value)}>
                {Object.entries(DOMAIN_LABELS).map(([value, label]) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Evidence period
              <select
                value={runMonth}
                onChange={(event) => {
                  setRunMonth(event.target.value);
                  setData(null);
                }}
              >
                {runs.map((run) => (
                  <option value={run.runMonth} key={run.runId}>
                    {run.runMonth}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Review state
              <select
                value={state}
                onChange={(event) => {
                  setState(event.target.value);
                  setData(null);
                }}
              >
                <option value="all">All review states</option>
                <option value="actionable">Ready for outreach</option>
                <option value="suppressed">Held or routed to review</option>
              </select>
            </label>
            <label>
              Exact person identifier
              <input
                value={memberId}
                onChange={(event) => {
                  setMemberId(event.target.value);
                  setData(null);
                }}
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                aria-describedby="restricted-id-guidance"
              />
            </label>
            <button className="bi-btn-primary" disabled={busy || !runMonth} onClick={() => search(1)}>
              {busy ? "Searching…" : "Search restricted data"}
            </button>
          </div>
          <p id="restricted-id-guidance" className="restricted-search-guidance">
            Search values stay in this restricted request and are not added to the URL, aggregate filters, or
            campaign records.
          </p>
        </Panel>

        {error && <ErrorCard error={error} />}
        {busy && !data && <Loading />}
        {data && (
          <Panel
            title={`${formatCount(data.total)} restricted opportunity ${data.total === 1 ? "record" : "records"}`}
            desc={`Page ${data.page} · up to ${data.pageSize} records per page · results are not retained in aggregate workflows`}
            headerRight={
              <button className="bi-btn-ghost" disabled={busy} onClick={clearResults}>
                Clear restricted results
              </button>
            }
          >
            <div className="restricted-capture-warning" role="note">
              <StatusPill status="blocked" label="Do not capture or transfer" />
              <span>
                Review in place. Do not paste these results into notes, campaigns, exports, or documentation.
              </span>
            </div>
            <div className="restricted-results">
              {data.rows.map((row, index) => (
                <details className="restricted-result" key={`${row.memberId}-${row.opportunityId}-${index}`}>
                  <summary>
                    <span className="restricted-member">{row.memberId}</span>
                    <span>{row.opportunityName || row.opportunityType || row.programDomain}</span>
                    <span>
                      Stage {row.stageId}: {row.stageName}
                    </span>
                    <span className={row.guardrail ? "restricted-guardrail" : "restricted-actionable"}>
                      {row.guardrail ? "Guardrail" : row.clinicalScoreBand || "Ready for outreach"}
                    </span>
                  </summary>
                  <div className="restricted-detail-grid">
                    <Detail label="Opportunity identifier" value={row.opportunityId} />
                    <Detail label="Condition group" value={row.conditionSubdomain} />
                    <Detail
                      label="Priority"
                      value={
                        row.priorityScore == null
                          ? "Unavailable"
                          : `${row.priorityScore} (rank ${row.priorityRank ?? "unavailable"})`
                      }
                    />
                    <Detail
                      label="Clinical score"
                      value={`${row.clinicalScoreName || "Unavailable"}: ${row.clinicalScoreValue ?? "Unavailable"}`}
                    />
                    <Detail label="Confidence" value={row.confidenceLevel} />
                    <Detail
                      label="Signal window"
                      value={`${row.firstSignalDate || "Unavailable"} to ${row.lastSignalDate || "Unavailable"}`}
                    />
                    <Detail label="Source claims" value={row.sourceClaimCount} />
                    <Detail
                      label="Source allowed amount"
                      value={
                        row.sourceAllowedAmount == null
                          ? "Unavailable"
                          : Number(row.sourceAllowedAmount).toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                            })
                      }
                    />
                    <Detail wide label="Trigger and evidence summary" value={row.evidenceSummary} />
                    <Detail wide label="Guardrail reasons" value={row.guardrailReasons} />
                    <Detail wide label="Suppression reason" value={row.suppressionReason} />
                    <Detail wide label="Recommended action" value={row.recommendedAction} />
                  </div>
                </details>
              ))}
              {!data.rows.length && (
                <p className="restricted-empty-result">
                  No restricted records match the selected review criteria.
                </p>
              )}
            </div>
            <div className="restricted-pagination" aria-label="Restricted result pages">
              <button disabled={busy || data.page <= 1} onClick={() => search(data.page - 1)}>
                Previous
              </button>
              <span>Page {data.page}</span>
              <button
                disabled={busy || data.page * data.pageSize >= data.total}
                onClick={() => search(data.page + 1)}
              >
                Next
              </button>
            </div>
          </Panel>
        )}
      </div>
    </Shell>
  );
}

function Detail({ label, value, wide = false }) {
  return (
    <div className={wide ? "restricted-detail wide" : "restricted-detail"}>
      <span>{label}</span>
      <strong>{value === null || value === undefined || value === "" ? "Unavailable" : String(value)}</strong>
    </div>
  );
}
